<script setup lang="ts">
import MediaButton from '@/components/shared/MediaButton.vue';
import {
  IDocument,
  IGallery,
  IImage,
  IVideo,
  MediaButtonType,
  IProfile,
} from '@/interfaces';
import DocumentsOverlay from '@/overlays/DocumentsOverlay.vue';
import GalleriesOverlay from '@/overlays/GalleriesOverlay.vue';
import PicturesOverlay from '@/overlays/PicturesOverlay.vue';
import VideosOverlay from '@/overlays/VideosOverlay.vue';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import { useInstitutionStore } from '@/stores/institution';
import { computed, ref } from 'vue';

interface Props {
  pictures?: IImage[];
  videos?: IVideo[];
  documents?: IDocument[];
  galleries?: IGallery[];
  needsAuth?: boolean;
  color: string;
  allowArchiveRemove?: boolean;
  isPersonalMedia?: boolean;
  limitedToProfile?: IProfile | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  pictures: () => [],
  videos: () => [],
  documents: () => [],
  galleries: () => [],
  needsAuth: false,
});

const institutionStore = useInstitutionStore();
const showPicturesOverlay = ref<boolean>(false);
const showVideosOverlay = ref<boolean>(false);
const showDocumentsOverlay = ref<boolean>(false);
const showGalleriesOverlay = ref<boolean>(false);

const showAuthFlow = ref<boolean>(false);
const selectedMediaType = ref<MediaButtonType>('pictures');

const galleryPasswordProtected = computed(() => {
  return institutionStore.settings?.screen?.galleryPasswordProtected || false;
});

const showMediaOverlay = () => {
  switch (selectedMediaType.value) {
    case 'pictures':
      showPicturesOverlay.value = true;
      break;
    case 'videos':
      showVideosOverlay.value = true;
      break;
    case 'documents':
      showDocumentsOverlay.value = true;
      break;
    case 'galleries':
      showGalleriesOverlay.value = true;
      break;
  }
};

const mediaButtonClicked = (mediaType: MediaButtonType) => {
  selectedMediaType.value = mediaType;

  if (
    props.needsAuth ||
    (mediaType === 'galleries' && galleryPasswordProtected.value === true)
  ) {
    showAuthFlow.value = true;
    return;
  }

  showMediaOverlay();
};

const accessGranted = () => {
  showAuthFlow.value = false;
  showMediaOverlay();
};
</script>

<template>
  <div class="relative flex items-center w-full mb-8">
    <MediaButton
      v-if="pictures.length"
      :count="pictures.length"
      :icon="['far', 'fa-image']"
      :color="color"
      @media-selected="mediaButtonClicked('pictures')"
    />

    <MediaButton
      v-if="videos.length"
      :count="videos.length"
      :icon="['fas', 'fa-film']"
      :color="color"
      @media-selected="mediaButtonClicked('videos')"
    />

    <MediaButton
      v-if="documents.length"
      :count="documents.length"
      :icon="['fas', 'fa-file-alt']"
      :color="color"
      @media-selected="mediaButtonClicked('documents')"
    />

    <MediaButton
      v-if="galleries.length"
      :count="galleries.length"
      :icon="['fas', 'fa-photo-video']"
      :color="color"
      @media-selected="mediaButtonClicked('galleries')"
    />
  </div>

  <PicturesOverlay
    v-if="showPicturesOverlay"
    :pictures="pictures"
    :is-personal-media="isPersonalMedia"
    @close="showPicturesOverlay = false"
  />

  <VideosOverlay
    v-if="showVideosOverlay"
    :videos="videos"
    :is-personal-media="isPersonalMedia"
    @close="showVideosOverlay = false"
  />

  <DocumentsOverlay
    v-if="showDocumentsOverlay"
    :documents="documents"
    :is-personal-media="isPersonalMedia"
    @close="showDocumentsOverlay = false"
  />

  <GalleriesOverlay
    v-if="showGalleriesOverlay"
    :galleries="galleries"
    :is-personal-media="isPersonalMedia"
    @close="showGalleriesOverlay = false"
  />

  <AuthFlow
    v-if="showAuthFlow"
    :title="$t('global.enterPinToAccess')"
    :limited-to-profile="limitedToProfile || undefined"
    @close="showAuthFlow = false"
    @authenticated="accessGranted"
  />
</template>
